/**
 * The output panel on «Общая библиотека» — the whole of what that screen says
 * about itself beyond one word, and deliberately drawn on the phone itself.
 *
 * There is no other window into this. The app is installed from TestFlight onto
 * a phone with no Mac attached, so `CAPLog.print` and every `os_log` under it
 * goes somewhere nobody can read; a person who thinks the backup is not working
 * has a screenshot and nothing else. So both halves report here: this file
 * timestamps and keeps the lines, `GallerySync.drainEvents` fills them with what
 * the engine did — checked against the server, sent, skipped, waited — and the
 * screen prints them in order.
 *
 * The bridge report below is the other half, and it is why the panel was first
 * built. A grey button and no system dialog is produced by at least four
 * unrelated faults that the screen cannot otherwise tell apart:
 *
 *   1. the plugin was never registered, so the JS proxy rejects every call with
 *      «not implemented» and the screen only knows that something failed;
 *   2. it is registered but the bridge never answers, so calls hang until the
 *      timeout in gallery.ts turns them into a sentence;
 *   3. everything works and iOS has already been asked once, so there is no
 *      second dialog to show and `requestPermission` resolves at once with
 *      `denied` — a press that correctly does nothing;
 *   4. the web bundle is not the native one at all (`isNative` false), so the
 *      plugin is never even looked for.
 *
 * Each leaves a different line below. What that report no longer does is print
 * five lines about a bridge that is fine: when everything is where it should be
 * it is one line, because the panel now belongs to the sync.
 */
import { apiOrigin, isNative } from '../api/origin.ts';
import { withTimeout } from './gallery.ts';
import { reportGallerySyncLine } from './gallerySyncReport.ts';

export interface DebugLine {
  /**
   * Milliseconds since the epoch. Absolute rather than «seconds since this
   * screen opened», which is what it used to be: half the lines here now come
   * from the engine, and the ones a background run wrote happened hours before
   * anybody opened the screen that prints them.
   */
  at: number;
  text: string;
}

/**
 * Replaced rather than mutated on every append: `useSyncExternalStore` compares
 * snapshots by identity, and an array pushed into in place never looks changed.
 */
let lines: readonly DebugLine[] = [];
const listeners = new Set<() => void>();

/** Enough to hold a session's worth of syncing without growing forever. */
const LIMIT = 300;

/**
 * Several lines at once — how the engine's drained events arrive.
 *
 * Sorted rather than appended, because those events carry the time they
 * happened and a background run's are older than everything already on screen.
 * `sort` is stable, so lines sharing a millisecond keep the order they came in.
 */
export function galleryLogAll(entries: readonly DebugLine[]): void {
  if (entries.length === 0) return;
  const next = [...lines, ...entries].sort((a, b) => a.at - b.at);
  lines = next.length > LIMIT ? next.slice(next.length - LIMIT) : next;
  for (const listener of listeners) listener();
}

/**
 * One line from this side of the bridge — to the panel, and to the server.
 *
 * Both, and only from here. `galleryLogAll` above is also fed by the engine's
 * drained events, and those are already on their way to `gallery_sync_log` from
 * Swift with a run id and the counts attached; teeing them here as well would
 * file every one of them twice, the second time worse.
 *
 * `event` is the code the row is grouped under in SQL. It defaults to the
 * unremarkable case; what is worth naming is a press — «what clicked» is the
 * first question asked of a sync that never started, and this is the only place
 * that can answer it.
 */
export function galleryLog(text: string, event = 'ui'): void {
  galleryLogAll([{ at: Date.now(), text }]);
  reportGallerySyncLine(text, event);
}

export function galleryLogSnapshot(): readonly DebugLine[] {
  return lines;
}

export function subscribeGalleryLog(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * One line as it is printed. Wall clock rather than an offset: it is the only
 * stamp that means the same thing for a line written here, a line written by a
 * background run last night, and the server log somebody is comparing it with.
 */
export function formatGalleryLine(line: DebugLine): string {
  const at = new Date(line.at);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}  ${line.text}`;
}

/** The whole log as one block of text, for the clipboard. */
export function formatGalleryLog(): string {
  return lines.map(formatGalleryLine).join('\n');
}

/** Just the shape of `window.Capacitor` this file reads. */
interface CapacitorGlobal {
  getPlatform?: () => string;
  isNativePlatform?: () => boolean;
  /** Injected by JSExport.exportJS, once per registered native plugin. */
  PluginHeaders?: Array<{ name: string; methods: Array<{ name: string }> }>;
  Plugins?: Record<string, unknown>;
}

function capacitorGlobal(): CapacitorGlobal | undefined {
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

/**
 * The methods the shell announced for the Gallery plugin, or null when it
 * announced no such plugin at all.
 *
 * `PluginHeaders` is decisive and free. Capacitor builds it in Swift — one entry
 * per plugin it registered, with its methods, pushed into the page by a
 * `WKUserScript` at document start — so this answers «does this app have
 * `drainEvents`?» without calling it and discovering the answer the expensive
 * way: an unknown method is not refused by the bridge, it is never answered at
 * all (see `withTimeout` in gallery.ts).
 */
export function galleryPluginMethods(): string[] | null {
  const gallery = capacitorGlobal()?.PluginHeaders?.find((header) => header.name === 'Gallery');
  return gallery ? gallery.methods.map((method) => method.name) : null;
}

/**
 * What the native half told the page about itself, at load.
 *
 * One line when everything is where it should be, and the full inventory only
 * when it is not: this panel is now read to find out why photographs are not
 * arriving, and five lines about a bridge that is fine push the answer off the
 * top of it every time the screen is opened.
 */
export function logBridgeReport(): void {
  const cap = capacitorGlobal();
  if (!isNative || !cap) {
    galleryLog(
      `не в оболочке: native=${isNative ? 'да' : 'НЕТ'}, window.Capacitor=${cap ? 'есть' : 'нет'}`,
      'bridge',
    );
    return;
  }

  const methods = galleryPluginMethods();
  if (methods) {
    galleryLog(
      `${cap.getPlatform?.() ?? '?'}, сервер ${apiOrigin || '(пусто)'}, плагин на месте`,
      'bridge',
    );
    return;
  }

  galleryLog('плагин Gallery не объявлен этой сборкой приложения', 'bridge');
  const headers = cap.PluginHeaders;
  galleryLog(
    headers
      ? `объявлены: ${headers.map((header) => header.name).join(', ') || '(ни одного)'}`
      : 'PluginHeaders нет вообще — ни один плагин не объявлен',
    'bridge',
  );
  const injected = cap.Plugins ? Object.keys(cap.Plugins) : null;
  galleryLog(
    `Capacitor.Plugins: ${injected === null ? 'нет' : injected.join(', ') || '(пусто)'}`,
    'bridge',
  );
}

/** How a value from the bridge is written into the log. */
function describe(value: unknown): string {
  if (value === undefined) return 'undefined';
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * One bridge call, with its outcome and how long it took.
 *
 * The elapsed time is half the diagnosis: a `requestPermission` that answers
 * `denied` in four milliseconds never showed anybody a dialog, and one that
 * answers `full` after nine seconds showed one and was read.
 */
export async function traced<T>(what: string, work: Promise<T>, ms: number): Promise<T> {
  const began = performance.now();
  const elapsed = () => `${Math.round(performance.now() - began)} мс`;
  galleryLog(`→ ${what}`, 'call');
  try {
    const value = await withTimeout(work, ms, what);
    galleryLog(`← ${what}: ${describe(value)} (${elapsed()})`, 'call');
    return value;
  } catch (error) {
    galleryLog(
      `✕ ${what}: ${error instanceof Error ? error.message : String(error)} (${elapsed()})`,
      'call.fail',
    );
    throw error;
  }
}
